import React, { useEffect, useRef, useState} from 'react';
import { Device } from "@twilio/voice-sdk";
import { Mic, MicOff, Phone } from 'lucide-react';
import { exchangeDate, notify } from '../../helpers';
import { fetchCallNoteAllQuestions, setupDevice } from '../../services/callNotes';
import Spacing from '../Spacing';
import ChronicConditions from '../UI/ChronicConditions';
import { Drawer } from 'vaul';
const userImage = './images/avatar.png';

export default function CallNoteCallModal({
  account_id,
  location_id,
  patient_data = {},
  callNoteCallType,
  activeModal = false,
  setActiveModal = () => {},
  setOutreachOpen = () => {},
  setActivePatientViewModal = () => {},
  startSchedular = false,
  setStartSchedular = () => {},
  smsLimit = {},
  setCallId = () => {},
  callFrom = "",
  previousCallScrollPosition = 0,
}) {
  const [isCalling, setIsCalling] = useState(false);
  const [device, setDevice] = useState(null);
  const [call, setCall] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [patientChronicConditions, setPatientChronicConditions] = useState([]);
  const [callNoteQuestionData, setCallNoteQuestionData] = useState([]);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);
  const isFetched = useRef(false);
  const callIntervalRef = useRef(null);
  const dropdownRef = useRef(null);
  
  const patientId = (callFrom === "waiting") ? patient_data.patient_id : patient_data.id;

  useEffect(() => {
    if (!isFetched.current && patientId && callNoteCallType) {
      if(callNoteCallType === "ccm") {
        fetchCallNoteQuestions(patientId);
      } else {
        setLoading(false);
      }
      isFetched.current = true;
    }
    return () => {
      clearInterval(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);


  const fetchCallNoteQuestions = async (patient_id) => {
    setLoading(true);
    try {
      const questionData = {
        account_id: account_id,
        location_id: location_id,
        patient_id: patient_id,
      };

      const response = await fetchCallNoteAllQuestions(questionData);
      if(response){
        if(response?.error) {
          notify("error", response?.error, 2);
          setActiveModal(false);
          window.scrollTo(0, previousCallScrollPosition);
          return;
        }
        if(response?.data) {
          setCallNoteQuestionData(response?.data?.ccm_questions);
          setPatientChronicConditions(response?.data?.patient_data?.chronic_conditions);
        } else {
          notify("error", response?.detail, 2);
          setActiveModal(false);
          window.scrollTo(0, previousCallScrollPosition);
        }
      }
    } catch (error) {
      // console.log("🚀 ~ fetchCallNoteAllQuestions ~ error:", error);
      setActiveModal(false);
      window.scrollTo(0, previousCallScrollPosition);
    } finally {
      setLoading(false);
    }
  }

  const fetchTokenAndSetupDevice = async () => {
    try {
      const response = await setupDevice();

      if(response) {
        const { token, identity } = response.data;

        const newDevice = new Device(token, { debug: true });

        newDevice.on("ready", () => {
          console.log(`Twilio Device ready for identity: ${identity}`);
        });
        newDevice.on("error", (error) => {
          console.error("Twilio Device error:", error);
        });
        newDevice.on("connect", () => {
          console.log("Twilio Device connected.");
          notify("success", "Call connected.", 2);
          setIsConnected(true);
          startCallTimer();
          setIsCalling(false);
        });

        newDevice.on("disconnect", () => {
          console.log("Twilio Device disconnected.");
          notify("info", "Call disconnected.", 2);
          setIsConnected(false);
          stopCallTimer();
          setIsCalling(false);
        });

        setDevice(newDevice);
      }

    } catch (error) {
      console.error("Failed to fetch Twilio token:", error);
      notify("error", "Failed to initialize Twilio. Please try again.", 2);
    }
  };

  useEffect(() => {
    fetchTokenAndSetupDevice();
    // handleCall();
    return () => {
      if (device) {
        device.destroy();
        setDevice(null);
      }
      stopCallTimer(); // Ensure the timer is stopped on cleanup
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const handleCall = async () => {
    if (!device) {
      notify("error", "Device not initialized. Please refresh and try again.", 2);
      return;
    }
    setIsCalling(true); // Show calling state

    const params = { To: patient_data?.mobilePhone, ...(smsLimit?.dummy_call === true && { Test: true }), AccountID: account_id, LocationID: location_id, PatientID: patientId, CallType: callNoteCallType }; // Pass the patient's phone number as a parameter
    try {
      const newCall = await device.connect({ params });
      
      notify("success", "Calling...", 2);
      // setIsCalling(true); // Show calling state
      setIsConnected(true);
      setIsCalling(false);
      setCall(newCall);
      startCallTimer(); // Start the call timer

      // Attach event listener for disconnect
      newCall.on("disconnect", handleDisconnect);

    } catch (error) {
      console.error("Error while calling device.connect:", error);
      notify("error", "Failed to initiate call. Please try again.", 2);
      setIsCalling(false); // Reset calling state on error
    }
  };

  const handleDisconnect = () => {
    if (device) {
      device.disconnectAll();
      setIsConnected(false); // Explicitly set isConnected to false on disconnect
      setIsCalling(false); // Explicitly set isCalling to false on disconnect
      stopCallTimer();
      if(startSchedular) {
        setOutreachOpen(true);
        setCallId(call?.parameters?.CallSid);
        setActivePatientViewModal(true);
        setActiveModal(!activeModal);
        window.scrollTo(0, previousCallScrollPosition);
      }
    }
  };

  const handleMuteToggle = async () => {
    if (device && call) {
      try {
        const resolvedCall = await call; // Await the resolved promise
        console.log("Call object:", resolvedCall);
  
        if (resolvedCall) {
          if (isMuted) {
            resolvedCall.mute(false); // Unmute
            notify("success", "Unmuted.", 2);
          } else {
            resolvedCall.mute(true); // Mute
            notify("success", "Muted.", 2);
          }
          setIsMuted(!isMuted);
        }
      } catch (error) {
        console.error("Error toggling mute:", error);
        notify("error", "Unable to toggle mute. Please try again.", 2);
      }
    } else {
      console.error("Device or call not initialized.");
    }
  };

  const startCallTimer = () => {
    if (callIntervalRef.current) return; // Prevent multiple intervals
    callIntervalRef.current = setInterval(() => {
      setStartSchedular(true);
      setCallDuration((prev) => prev + 1);
    }, 1000);
  };

  const stopCallTimer = () => {
    if (callIntervalRef.current) {
      clearInterval(callIntervalRef.current);
      callIntervalRef.current = null;
    }
    setCallDuration(0); // Reset the call duration
  };

  const formatCallDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleCloseModal = () => {
    handleDisconnect();
    setActiveModal(!activeModal);
    window.scrollTo(0, previousCallScrollPosition);
  };
  

  return (
    <div style={{ height: "100%" }} ref={dropdownRef}>
      <Drawer.Root open={activeModal} onOpenChange={handleCloseModal}>
        <Drawer.Trigger className="docpro_single_patiant_modal_btn">
        </Drawer.Trigger>
        <Drawer.Portal>
          <Drawer.Overlay className="docpro_drawer_overlay"/>
          <Drawer.Content className="docpro_drawer_content docpro_drawer_style_4">
            <Drawer.Title className="sr-only">Calling Modal</Drawer.Title>
            <Drawer.Description className="sr-only">View details of a parient call</Drawer.Description>
            <div className="docpro_modal_wrap docpro_style_1 docpro_modal_lg">
              <div className={`docpro_modal custom-scroll call_note_screen ${callNoteCallType === "ccm" ? 'ccm' : 'schedular'}`}>
                <div className={`calling-screen ${callNoteCallType === "ccm" ? 'ccm_screen' : 'schedular_screen'}`}>
                  <div>
                    <div className="user-avatar">
                      <img src={userImage} alt="User Avatar" />
                    </div>
                    <h2 className="user-name">{patient_data?.lastName} {patient_data?.firstName}</h2>
                    <div className='call_note_process'>
                      {isCalling &&
                        <p className="call-status">Calling ...</p>
                      }
                      {isConnected && !isCalling && (
                        
                        <div className='call-duration'>
                          <p>{formatCallDuration(callDuration)}</p>
                          <div className="talking-animation">
                            <div className="bar"></div>
                            <div className="bar"></div>
                            <div className="bar"></div>
                            <div className="bar"></div>
                            <div className="bar"></div>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className='calling_buttons'>
                      {!isConnected && !isCalling && (
                        <button className="start-call-button" onClick={() => handleCall()}><Phone width={20} height={20} /> Start Call</button>
                      )}
                      {isConnected && (
                        <>
                          <button className="mute-call-button" onClick={() => handleMuteToggle()}>
                            {!isMuted ? 
                              <><MicOff width={20} height={20} /> Mute</>
                            : 
                              <><Mic width={20} height={20} /> Unmute</>
                            }
                          </button>
                          <button className="end-call-button" onClick={() => handleDisconnect()}><Phone width={20} height={20} /> End Call</button>
                        </>
                      )}
                    </div>
                    
                    <div className='ccm_questions'>
                      {loading ? (
                        <div
                          style={{
                            height: "360px",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                          }}
                        >
                          CCM Questions...
                        </div>
                      ) : (
                        callNoteQuestionData?.length > 0 && (
                          <>
                            <hr />
                            <Spacing  lg="30" sm="30" />
                            <div className='ccm_section'>
                              <div className='patient_info'>
                                <h3 className='docpro_fs_18'>Patient Info</h3>
                                <div>
                                  <p className='docpro_mb_0 mb-1'><b>Name: </b>{patient_data?.lastName} {patient_data?.firstName}</p>
                                  <ChronicConditions conditions={patientChronicConditions} />
                                  <p className='docpro_mb_0 mb-1'><b>DOB: </b>{exchangeDate(patient_data?.dob)}</p>
                                  <p className='docpro_mb_0 mb-1'><b>Gender: </b>{patient_data?.sex}</p>
                                  <p className='docpro_mb_0 mb-1'><b>Last Visit: </b>{exchangeDate(patient_data?.lastVisit)}</p>
                                  <p className='docpro_mb_0 mb-1'><b>Insurance: </b>{patient_data?.insuranceName}</p>
                                </div>
                                <Spacing  lg="30" sm="30" />
                              </div>

                              <div>
                                <h3 className='docpro_fs_18'>CCM Questions</h3>
                                {callNoteQuestionData?.map((item, index) => (
                                  <div key={index}>
                                    <p className="docpro_mb_0"><b style={{ textTransform: 'captionSide' }}>{index + 1}. {item?.condition} </b></p>
                                    <p style={{ marginLeft: '20px' }}>{item?.question}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </div>
  );
}